/**
 * useSpeakerBinding Hooks
 *
 * React Query hooks for speaker binding workflow including fetching audio snippets,
 * confirming speaker identities, swapping speakers, and checking workflow status.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getBindingSnippets,
  confirmBinding,
  confirmBindingAndAnalyze,
  swapSpeakersAtTimestamp,
  getWorkflowStatus,
} from "@/services/speakerBinding";
import type { ConfirmAndAnalyzeResponse } from "@/services/speakerBinding";
import type {
  SpeakerSnippetsResponse,
  ConfirmBindingRequest,
  ConfirmBindingResponse,
  SwapAtTimestampRequest,
  SwapAtTimestampResponse,
  BindingWorkflowStatus,
} from "@/types/speaker-binding";
import type { TranscriptWithSegmentsResponse } from "@/types/transcript";

/**
 * Hook to fetch representative audio snippets for speaker binding wizard
 *
 * Fetches 2-4 high-quality audio clips per speaker for user confirmation.
 */
export function useBindingSnippets(
  transcriptId: string | undefined,
  options: { enabled?: boolean } = {}
) {
  const { enabled = true } = options;

  return useQuery<SpeakerSnippetsResponse, Error>({
    queryKey: ["binding-snippets", transcriptId],
    queryFn: () => getBindingSnippets(transcriptId!),
    enabled: !!transcriptId && enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error) => {
      // Don't retry on 404 (transcript not found)
      if (
        (error as { response?: { status?: number } })?.response?.status === 404
      ) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

/**
 * Hook to confirm speaker binding with user-provided names and pronouns
 *
 * Returns a mutation that updates the speaker binding and invalidates related queries.
 * Pass recordingId to properly invalidate useTranscript queries (which use recordingId).
 * Pass reflectionId to invalidate feedback queries (speaker tokens are replaced at fetch time).
 */
export function useConfirmBinding(
  transcriptId: string,
  recordingId?: string | null,
  reflectionId?: string | null
) {
  const queryClient = useQueryClient();

  return useMutation<ConfirmBindingResponse, Error, ConfirmBindingRequest>({
    mutationFn: (request: ConfirmBindingRequest) =>
      confirmBinding(transcriptId, request),
    onSuccess: () => {
      // Invalidate queries that depend on binding status (use transcriptId)
      queryClient.invalidateQueries({
        queryKey: ["binding-snippets", transcriptId],
      });
      queryClient.invalidateQueries({
        queryKey: ["workflow-status", transcriptId],
      });
      // Invalidate transcript/speakers queries (use recordingId if available)
      if (recordingId) {
        queryClient.invalidateQueries({
          queryKey: ["transcript", recordingId],
        });
        queryClient.invalidateQueries({ queryKey: ["speakers", recordingId] });
      }
      // Invalidate annotations queries (speaker tokens are replaced at fetch time)
      // Use predicate to match all annotation queries for this transcript
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === "annotations" &&
          query.queryKey[1] === transcriptId,
      });
      // Invalidate feedback query if reflectionId is available
      if (reflectionId) {
        queryClient.invalidateQueries({
          queryKey: ["feedback", reflectionId],
        });
      }
    },
  });
}

/**
 * Hook to swap speaker labels at a specific timestamp
 *
 * Used when speakers switched positions mid-recording.
 * Returns a mutation that swaps speakers and invalidates related queries.
 */
export function useSwapSpeakersAtTimestamp(transcriptId: string) {
  const queryClient = useQueryClient();

  return useMutation<SwapAtTimestampResponse, Error, SwapAtTimestampRequest>({
    mutationFn: (request: SwapAtTimestampRequest) =>
      swapSpeakersAtTimestamp(transcriptId, request),
    onSuccess: () => {
      // Invalidate queries that depend on speaker assignments
      queryClient.invalidateQueries({
        queryKey: ["binding-snippets", transcriptId],
      });
      queryClient.invalidateQueries({
        queryKey: ["workflow-status", transcriptId],
      });
      queryClient.invalidateQueries({ queryKey: ["transcript", transcriptId] });
      queryClient.invalidateQueries({ queryKey: ["speakers", transcriptId] });
    },
  });
}

/**
 * Hook to check if speaker binding wizard is required
 *
 * Determines whether to trigger the wizard based on binding status,
 * speaker count, and confidence scores.
 */
export function useWorkflowStatus(
  transcriptId: string | undefined,
  options: { enabled?: boolean } = {}
) {
  const { enabled = true } = options;

  return useQuery<BindingWorkflowStatus, Error>({
    queryKey: ["workflow-status", transcriptId],
    queryFn: () => getWorkflowStatus(transcriptId!),
    enabled: !!transcriptId && enabled,
    staleTime: 1 * 60 * 1000, // 1 minute
    retry: (failureCount, error) => {
      // Don't retry on 404
      if (
        (error as { response?: { status?: number } })?.response?.status === 404
      ) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

/**
 * Hook to confirm speaker binding and trigger analysis pipeline continuation
 *
 * Used when pipeline is paused awaiting speaker confirmation. Confirms binding
 * and queues feedback/insights generation.
 */
export function useConfirmAndAnalyze(
  transcriptId: string,
  recordingId?: string | null,
  reflectionId?: string | null
) {
  const queryClient = useQueryClient();

  return useMutation<ConfirmAndAnalyzeResponse, Error, ConfirmBindingRequest>({
    mutationFn: (request: ConfirmBindingRequest) =>
      confirmBindingAndAnalyze(transcriptId, request),
    onSuccess: (data) => {
      // Immediately update transcript cache with confirmed status
      // This ensures the UI updates instantly rather than waiting for refetch
      if (recordingId) {
        queryClient.setQueryData<TranscriptWithSegmentsResponse>(
          ["transcript", recordingId],
          (oldData) => {
            if (oldData) {
              return {
                ...oldData,
                binding_status:
                  data.binding_status as TranscriptWithSegmentsResponse["binding_status"],
                speakers_confirmed_for_analysis:
                  data.speakers_confirmed_for_analysis,
              };
            }
            return oldData;
          }
        );
      }

      // Invalidate queries that depend on binding status
      queryClient.invalidateQueries({
        queryKey: ["binding-snippets", transcriptId],
      });
      queryClient.invalidateQueries({
        queryKey: ["workflow-status", transcriptId],
      });
      // Invalidate speakers queries to get updated binding status
      if (recordingId) {
        queryClient.invalidateQueries({ queryKey: ["speakers", recordingId] });
      }
      // Invalidate reflection query to get updated recording status
      if (reflectionId) {
        queryClient.invalidateQueries({
          queryKey: ["reflection", reflectionId],
        });
        queryClient.invalidateQueries({
          queryKey: ["feedback", reflectionId],
        });
      }
      // Also invalidate using the reflection_id returned from the API
      if (data.reflection_id) {
        queryClient.invalidateQueries({
          queryKey: ["reflection", data.reflection_id],
        });
      }
    },
  });
}
